/* eslint-disable @typescript-eslint/no-explicit-any */
import { ChangeDetectionStrategy, Component, ViewChild } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { SecurityService, StaffService } from '@wsphere/staff/web';
import { combineLatest, from, Observable, shareReplay, switchMap } from 'rxjs';
import { DistributorTableComponent } from '../../components';
import { CreateDistributorDialog, CreateDistributorOnboardingLinkDialog } from '../../dialogs';

@Component({
  changeDetection: ChangeDetectionStrategy.Eager,
  templateUrl: './distributors.page.html',
  standalone: false,
})
export class DistributorsPage {
  constructor(
    private router: Router,
    private route: ActivatedRoute,
    private staffSvc: StaffService,
    private securitySvc: SecurityService
  ) {}

  @ViewChild('table') table?: DistributorTableComponent;

  @ViewChild('editDistributorDialog') editDistributorDialog?: CreateDistributorDialog;
  @ViewChild('createDistributorDialog') createDistributorDialog!: CreateDistributorDialog;
  @ViewChild('createLinkDialog') createLinkDialog?: CreateDistributorOnboardingLinkDialog;

  staff$ = from(this.staffSvc.retrieveSelf()).pipe(shareReplay(1));

  protected canManageTags$ = this.staff$.pipe(
    switchMap(async () => {
      return await this.securitySvc
        .hasAccess('dist_ManageTags', null)
        .then(() => true)
        .catch(() => false);
    }),
    shareReplay(1)
  );

  protected canManageAccountInfo$ = this.staff$.pipe(
    switchMap(async () => {
      return await this.securitySvc.hasAccess('dist_ManageAccountInfo', null);
    }),
    shareReplay(1)
  );

  readonly canCreateDistributor$: Observable<boolean> = combineLatest([
    this.securitySvc.globalDistributors$,
    this.staff$,
  ]).pipe(
    switchMap(async () => {
      return await this.securitySvc.hasAccess('dist_CreateDistributor', null);
    })
  );

  readonly canOnboardDistributor$: Observable<boolean> = combineLatest([
    this.securitySvc.globalDistributors$,
    this.staff$,
  ]).pipe(
    switchMap(async ([globalDistributor]) => {
      const permissionKeys = globalDistributor?.map(dist => dist.permissionKey) ?? null;
      return await this.securitySvc.hasAccess('dist_OnboardDistributor', permissionKeys);
    })
  );

  openCreateDistributorDialog() {
    this.createDistributorDialog.openDialog();
  }

  navigateTo(distributorId?: string) {
    if (distributorId) this.router.navigate([`${distributorId}`], { relativeTo: this.route });
    else this.router.navigate([`../`], { relativeTo: this.route });
  }
}
